import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useDashboardActivityRealtime } from './useDashboardActivityRealtime';

export interface DashboardActivity {
  id: string;
  type: 'activity' | 'task' | 'lead' | 'project' | 'contact' | 'account_plan' | 'opportunity';
  activityType: string;
  title: string;
  entityName?: string;
  entityType?: string;
  entityId?: string;
  timestamp: string;
  icon: 'activity' | 'task-created' | 'task-completed' | 'task-updated' | 'lead-created' | 'project-created' | 'contact-created' | 'plan-created' | 'status-changed' | 'commented' | 'budget-changed' | 'opportunity-created' | 'opportunity-stage-changed' | 'opportunity-won';
  color: 'primary' | 'success' | 'warning' | 'info' | 'accent' | 'secondary' | 'destructive';
  metadata?: {
    oldValue?: string;
    newValue?: string;
    userName?: string;
    stageName?: string;
    estimatedValue?: number;
  };
}

const mapActivityToFeedItem = (activity: any): DashboardActivity => {
  const userName = activity.profile 
    ? `${activity.profile.first_name || ''} ${activity.profile.last_name || ''}`.trim() || 'Someone'
    : 'Someone';

  const baseActivity: DashboardActivity = {
    id: activity.id,
    type: 'activity',
    activityType: activity.activity_type,
    title: '',
    entityType: activity.entity_type,
    entityId: activity.entity_id,
    timestamp: activity.created_at,
    icon: 'activity',
    color: 'primary',
    metadata: {
      userName,
      oldValue: activity.old_value,
      newValue: activity.new_value,
    },
  };

  switch (activity.activity_type) {
    case 'created':
      baseActivity.title = `${userName} created a ${activity.entity_type}`;
      baseActivity.icon = 'activity';
      baseActivity.color = 'success';
      break;
    case 'status_changed':
      baseActivity.title = `${userName} changed status to "${activity.new_value}"`;
      baseActivity.icon = 'status-changed';
      baseActivity.color = 'info';
      break;
    case 'commented':
      baseActivity.title = `${userName} added a comment`;
      baseActivity.icon = 'commented';
      baseActivity.color = 'primary';
      break;
    case 'budget_changed':
      baseActivity.title = `${userName} updated the budget`;
      baseActivity.icon = 'budget-changed';
      baseActivity.color = 'accent';
      break;
    case 'priority_changed':
      baseActivity.title = `${userName} changed priority to "${activity.new_value}"`;
      baseActivity.icon = 'status-changed';
      baseActivity.color = 'warning';
      break;
    default:
      baseActivity.title = `${userName} updated a ${activity.entity_type}`;
      baseActivity.icon = 'activity';
      baseActivity.color = 'primary';
  }

  return baseActivity;
};

export function useDashboardActivity() {
  const { user } = useAuth();
  
  // Enable realtime subscriptions
  useDashboardActivityRealtime(!!user);

  const { data: activities, isLoading } = useQuery({
    queryKey: ['dashboard-activities', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Fetch all data in parallel
      const [activitiesRes, tasksRes, updatedTasksRes, leadsRes, projectsRes, contactsRes, accountPlansRes, opportunitiesRes, stageChangesRes] = await Promise.all([
        // Activities from activities table
        supabase
          .from('activities')
          .select(`
            *,
            profile:user_id (first_name, last_name)
          `)
          .eq('user_id', user.id)
          .gte('created_at', thirtyDaysAgo.toISOString())
          .order('created_at', { ascending: false })
          .limit(20),
        
        // Recently CREATED tasks
        supabase
          .from('tasks')
          .select('id, title, status, created_at, updated_at')
          .eq('user_id', user.id)
          .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
          .order('created_at', { ascending: false })
          .limit(10),
        
        // Recently UPDATED tasks (regardless of when created)
        supabase
          .from('tasks')
          .select('id, title, status, created_at, updated_at')
          .eq('user_id', user.id)
          .gte('updated_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
          .order('updated_at', { ascending: false })
          .limit(15),
        
        // Recent leads
        supabase
          .from('leads')
          .select('id, name, status, created_at')
          .eq('user_id', user.id)
          .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
          .order('created_at', { ascending: false })
          .limit(5),
        
        // Recent projects
        supabase
          .from('projects')
          .select('id, name, status, created_at, updated_at')
          .eq('user_id', user.id)
          .gte('created_at', new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString())
          .order('created_at', { ascending: false })
          .limit(5),
        
        // Recent contacts
        supabase
          .from('contacts')
          .select('id, name, created_at')
          .eq('user_id', user.id)
          .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
          .order('created_at', { ascending: false })
          .limit(5),
        
        // Recent account plans
        supabase
          .from('account_plans')
          .select('id, name, status, created_at')
          .eq('user_id', user.id)
          .gte('created_at', new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString())
          .order('created_at', { ascending: false })
          .limit(5),
        
        // Recent opportunities
        supabase
          .from('opportunities')
          .select(`
            id,
            name,
            estimated_value,
            created_at,
            pipeline_stage:pipeline_stages(
              id,
              name,
              win_probability
            )
          `)
          .eq('user_id', user.id)
          .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
          .order('created_at', { ascending: false })
          .limit(10),
        
        // Recent opportunity stage changes
        supabase
          .from('opportunity_stage_history')
          .select(`
            id,
            opportunity_id,
            changed_at,
            from_stage:pipeline_stages!opportunity_stage_history_from_stage_id_fkey(id, name, win_probability),
            to_stage:pipeline_stages!opportunity_stage_history_to_stage_id_fkey(id, name, win_probability),
            opportunity:opportunities!opportunity_stage_history_opportunity_id_fkey(
              id,
              name,
              user_id,
              estimated_value
            )
          `)
          .gte('changed_at', new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString())
          .order('changed_at', { ascending: false })
          .limit(15)
      ]);

      const combinedActivities: DashboardActivity[] = [];

      // Add activities from activities table
      if (activitiesRes.data) {
        combinedActivities.push(...activitiesRes.data.map(mapActivityToFeedItem));
      }

      // Track processed task IDs to avoid duplicates
      const processedTaskIds = new Set<string>();

      // Process recently UPDATED tasks first (these are more interesting)
      if (updatedTasksRes.data) {
        updatedTasksRes.data.forEach(task => {
          processedTaskIds.add(task.id);
          
          if (task.status === 'done') {
            combinedActivities.push({
              id: `task-completed-${task.id}`,
              type: 'task',
              activityType: 'task_completed',
              title: `Task completed`,
              entityName: task.title,
              entityType: 'task',
              entityId: task.id,
              timestamp: task.updated_at,
              icon: 'task-completed',
              color: 'success',
            });
          } else if (task.created_at !== task.updated_at) {
            // Task was updated (not just created)
            combinedActivities.push({
              id: `task-updated-${task.id}`,
              type: 'task',
              activityType: 'task_updated',
              title: `Task updated`,
              entityName: task.title,
              entityType: 'task',
              entityId: task.id,
              timestamp: task.updated_at,
              icon: 'task-updated',
              color: 'info',
            });
          }
        });
      }

      // Add recently created tasks (only if not already processed)
      if (tasksRes.data) {
        tasksRes.data.forEach(task => {
          if (processedTaskIds.has(task.id)) return;
          
          combinedActivities.push({
            id: `task-created-${task.id}`,
            type: 'task',
            activityType: 'task_created',
            title: `New task created`,
            entityName: task.title,
            entityType: 'task',
            entityId: task.id,
            timestamp: task.created_at,
            icon: 'task-created',
            color: 'primary',
          });
        });
      }

      // Add leads
      if (leadsRes.data) {
        leadsRes.data.forEach(lead => {
          combinedActivities.push({
            id: `lead-${lead.id}`,
            type: 'lead',
            activityType: 'lead_created',
            title: 'New lead',
            entityName: lead.name,
            entityType: 'lead',
            entityId: lead.id,
            timestamp: lead.created_at,
            icon: 'lead-created',
            color: 'warning',
          });
        });
      }

      // Add projects
      if (projectsRes.data) {
        projectsRes.data.forEach(project => {
          combinedActivities.push({
            id: `project-${project.id}`,
            type: 'project',
            activityType: 'project_created',
            title: 'Project started',
            entityName: project.name,
            entityType: 'project',
            entityId: project.id,
            timestamp: project.created_at,
            icon: 'project-created',
            color: 'info',
          });
        });
      }

      // Add contacts
      if (contactsRes.data) {
        contactsRes.data.forEach(contact => {
          combinedActivities.push({
            id: `contact-${contact.id}`,
            type: 'contact',
            activityType: 'contact_created',
            title: 'New contact added',
            entityName: contact.name,
            entityType: 'contact',
            entityId: contact.id,
            timestamp: contact.created_at,
            icon: 'contact-created',
            color: 'secondary',
          });
        });
      }

      // Add account plans
      if (accountPlansRes.data) {
        accountPlansRes.data.forEach(plan => {
          combinedActivities.push({
            id: `plan-${plan.id}`,
            type: 'account_plan',
            activityType: 'plan_created',
            title: 'Account plan created',
            entityName: plan.name,
            entityType: 'account_plan',
            entityId: plan.id,
            timestamp: plan.created_at,
            icon: 'plan-created',
            color: 'accent',
          });
        });
      }

      // Add opportunities (created)
      if (opportunitiesRes.data) {
        opportunitiesRes.data.forEach(opportunity => {
          const isHighValue = opportunity.estimated_value && opportunity.estimated_value >= 1000000;
          
          combinedActivities.push({
            id: `opportunity-${opportunity.id}`,
            type: 'opportunity',
            activityType: 'opportunity_created',
            title: isHighValue ? 'High-value opportunity created' : 'New opportunity',
            entityName: opportunity.name,
            entityType: 'opportunity',
            entityId: opportunity.id,
            timestamp: opportunity.created_at,
            icon: 'opportunity-created',
            color: isHighValue ? 'accent' : 'info',
            metadata: {
              estimatedValue: opportunity.estimated_value,
              stageName: opportunity.pipeline_stage?.name,
            },
          });
        });
      }

      // Add opportunity stage changes
      if (stageChangesRes.data) {
        stageChangesRes.data
          .filter(change => change.opportunity?.user_id === user.id)
          .forEach(change => {
            const isWon = change.to_stage?.win_probability === 100;
            
            combinedActivities.push({
              id: `stage-change-${change.id}`,
              type: 'opportunity',
              activityType: isWon ? 'opportunity_won' : 'opportunity_stage_changed',
              title: isWon 
                ? '🎉 Opportunity won!' 
                : `Opportunity moved to ${change.to_stage?.name || 'new stage'}`,
              entityName: change.opportunity?.name,
              entityType: 'opportunity',
              entityId: change.opportunity_id,
              timestamp: change.changed_at,
              icon: isWon ? 'opportunity-won' : 'opportunity-stage-changed',
              color: isWon ? 'success' : 'info',
              metadata: {
                stageName: change.to_stage?.name,
                oldValue: change.from_stage?.name,
                newValue: change.to_stage?.name,
                estimatedValue: change.opportunity?.estimated_value,
              },
            });
          });
      }

      // Sort by timestamp descending and limit to 15 items
      return combinedActivities
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 15);
    },
    enabled: !!user,
    staleTime: 30 * 1000, // 30 seconds (fallback for realtime)
  });

  return {
    activities: activities || [],
    isLoading,
  };
}
